import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2, GripVertical, FileText, ChevronDown, ChevronRight, Settings2 } from 'lucide-react';
import {
    valueTypes,
    fieldInputs,
    inputAllowedValueTypes,
    defaultFieldForValueType,
    commonProperties,
    fieldProperties,
    fieldConstraints,
    type ValueType,
    type FieldInput,
    type ExtensionDescriptor,
} from '../../../../shared/contracts/form-schema/field-type-registry';
import {
    deserializePssFormField,
    serializePssFormField,
    type PssFormField,
} from './serialization';

export type { PssFormField } from './serialization';

export interface PssFormBuilderData {
    formSchema?: {
        fields: unknown[];
        [key: string]: unknown;
    };
    formModifiers?: string;
    [key: string]: unknown;
}

export interface PssFormBuilderTabProps {
    data: PssFormBuilderData;
    onChange: (key: 'formSchema' | 'formModifiers', value: unknown) => void;
}

const VALUE_TYPE_LABELS: Record<ValueType, string> = {
    string: 'String',
    number: 'Number',
    bool: 'Boolean',
    date: 'Date',
    datetime: 'Date & Time',
    enum: 'Enumeration',
    present: 'Present (display only)',
};

const FIELD_INPUT_LABELS: Record<FieldInput, string> = {
    text: 'Text',
    textarea: 'Text Area',
    integer: 'Integer',
    float: 'Float',
    switch: 'Switch',
    terms: 'Terms (checkbox)',
    date: 'Date',
    datetime: 'Date & Time',
    dateRangeBegin: 'Date Range (Begin)',
    dateRangeEnd: 'Date Range (End)',
    dialog: 'Dialog',
    select: 'Dropdown (Select)',
    pdfViewer: 'PDF Viewer',
    badgePhoto: 'Badge Photo',
    badgePreviewFront: 'Badge Preview (Front)',
    badgePreviewBack: 'Badge Preview (Back)',
};

const inputCls = "w-full px-2 py-1.5 text-xs border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none";
const labelCls = "block text-[10px] font-bold text-slate-500 uppercase mb-1";
const extensionLabelCls = "block text-[10px] font-bold leading-none text-slate-500 uppercase";
const checkboxCls = "rounded border-slate-300 text-teal-600 focus:ring-teal-500 w-3.5 h-3.5";

const DescriptionTooltip: React.FC<{ id: string; description: string }> = ({ id, description }) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const updatePosition = useCallback(() => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;

        const halfTooltipWidth = 112;
        setPosition({
            top: rect.top - 8,
            left: Math.min(
                window.innerWidth - halfTooltipWidth - 8,
                Math.max(halfTooltipWidth + 8, rect.left + rect.width / 2),
            ),
        });
    }, []);

    const openTooltip = () => {
        updatePosition();
        setIsOpen(true);
    };

    useEffect(() => {
        if (!isOpen) return;
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    const tooltip = typeof document === 'undefined' ? null : createPortal(
        <span
            id={id}
            role="tooltip"
            className={`pointer-events-none fixed w-56 -translate-x-1/2 -translate-y-full rounded bg-slate-900 px-2.5 py-2 text-[10px] font-normal normal-case leading-relaxed text-white shadow-lg transition-opacity dark:bg-slate-700 ${isOpen ? 'visible opacity-100' : 'invisible opacity-0'}`}
            style={{ top: position.top, left: position.left, zIndex: 2147483647 }}
        >
            {description}
        </span>,
        document.body,
    );

    return (
        <span className="ml-0.5 inline-flex shrink-0 self-center" onMouseEnter={openTooltip} onMouseLeave={() => setIsOpen(false)}>
            <button
                ref={buttonRef}
                type="button"
                className="inline-flex h-3 w-3 items-center justify-center rounded-full border border-current text-[8px] font-semibold leading-none text-slate-400 transition-colors hover:text-teal-600 focus:text-teal-600 focus:outline-none"
                aria-label="Show field description"
                aria-describedby={id}
                onFocus={openTooltip}
                onBlur={() => setIsOpen(false)}
            >
                ?
            </button>
            {tooltip}
        </span>
    );
};

function resolveInput(field: PssFormField): FieldInput {
    if (field.input && inputAllowedValueTypes[field.input]?.includes(field.type)) {
        return field.input;
    }
    return defaultFieldForValueType[field.type];
}

function applicableInputs(type: ValueType): FieldInput[] {
    return fieldInputs.filter((input) => inputAllowedValueTypes[input].includes(type));
}

function formatPropertyLabel(name: string): string {
    return name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

// --- Sub-components ---

const StaticOptionsEditor: React.FC<{
    value: Record<string, unknown>;
    valueType: ValueType;
    onChange: (value: Record<string, unknown>) => void;
}> = ({ value, valueType, onChange }) => {
    const entries = Object.entries(value);

    const handleAdd = () => {
        const baseLabel = 'New option';
        let label = baseLabel;
        let suffix = 2;
        while (Object.hasOwn(value, label)) label = `${baseLabel} ${suffix++}`;
        onChange({ ...value, [label]: valueType === 'number' ? 0 : valueType === 'bool' ? false : '' });
    };

    const handleRemove = (label: string) => {
        const next = { ...value };
        delete next[label];
        onChange(next);
    };

    const handleChange = (oldLabel: string, newLabel: string, newValue: unknown) => {
        const next: Record<string, unknown> = {};
        for (const [label, optionValue] of Object.entries(value)) {
            next[label === oldLabel ? newLabel : label] = label === oldLabel ? newValue : optionValue;
        }
        onChange(next);
    };

    const renderValueInput = (label: string, optionValue: unknown) => {
        if (valueType === 'bool') {
            return (
                <select
                    value={optionValue === true ? 'true' : 'false'}
                    onChange={(event) => handleChange(label, label, event.target.value === 'true')}
                    className={inputCls}
                    aria-label={`Value for ${label}`}
                >
                    <option value="true">True</option>
                    <option value="false">False</option>
                </select>
            );
        }

        const inputType = valueType === 'number' ? 'number' : valueType === 'date' ? 'date' : valueType === 'datetime' ? 'datetime-local' : 'text';
        return (
            <input
                type={inputType}
                step={valueType === 'number' ? 'any' : undefined}
                value={typeof optionValue === 'string' || typeof optionValue === 'number' ? optionValue : ''}
                onChange={(event) => handleChange(
                    label,
                    label,
                    valueType === 'number' && event.target.value !== '' ? event.target.valueAsNumber : event.target.value,
                )}
                placeholder={valueType === 'number' ? '0' : valueType === 'date' ? 'YYYY-MM-DD' : valueType === 'datetime' ? 'Date and time' : 'Literal value'}
                className={inputCls}
                aria-label={`Value for ${label}`}
            />
        );
    };

    return (
        <div className="space-y-1.5">
            {entries.length > 0 && (
                <div className="grid grid-cols-[1fr_1fr_28px] gap-1.5 px-0.5 text-[9px] font-bold uppercase text-slate-400">
                    <span>Label</span><span>Value</span><span />
                </div>
            )}
            {entries.map(([label, optionValue], index) => (
                <div key={index} className="grid grid-cols-[1fr_1fr_28px] items-center gap-1.5">
                    <input
                        type="text"
                        value={label}
                        onChange={(event) => handleChange(label, event.target.value, optionValue)}
                        placeholder="Option label"
                        className={inputCls}
                        aria-label="Option label"
                    />
                    {renderValueInput(label, optionValue)}
                    <button type="button" onClick={() => handleRemove(label)} className="p-1 text-slate-400 transition-colors hover:text-red-500" aria-label={`Remove ${label}`}>
                        <Trash2 size={12} />
                    </button>
                </div>
            ))}
            <button type="button" onClick={handleAdd} className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-bold text-teal-600 transition-colors hover:bg-teal-50 dark:hover:bg-teal-900/30">
                <Plus size={10} /> Add
            </button>
        </div>
    );
};

/** Renders `options`: either a datasource name or a static `{label: typedValue}` object. */
const OptionsField: React.FC<{
    value: string;
    onChange: (value: string) => void;
    description: string;
    required?: boolean;
    descriptionId: string;
    fieldValueType: ValueType;
}> = ({ value, onChange, description, required, descriptionId, fieldValueType }) => {
    let parsed: Record<string, unknown> | null = null;
    try {
        const candidate = value ? JSON.parse(value) : null;
        if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) parsed = candidate;
    } catch {
        // Non-JSON values are datasource names.
    }
    const isStatic = parsed !== null;

    return (
        <div className="space-y-1.5">
            <div className="flex h-4 items-center justify-between">
                <div className="flex items-center">
                    <label className={extensionLabelCls}>Options{required ? ' *' : ''}</label>
                    <DescriptionTooltip id={descriptionId} description={description} />
                </div>
                <button type="button" onClick={() => onChange(isStatic ? '' : JSON.stringify({}))} className="text-[9px] font-bold text-teal-600 hover:underline">
                    {isStatic ? 'Switch to datasource' : 'Switch to static options'}
                </button>
            </div>
            {isStatic ? (
                <StaticOptionsEditor value={parsed || {}} valueType={fieldValueType} onChange={(next) => onChange(JSON.stringify(next))} />
            ) : (
                <input type="text" value={value || ''} onChange={(event) => onChange(event.target.value)} placeholder="Datasource name, e.g. Pool" className={inputCls} aria-describedby={descriptionId} />
            )}
        </div>
    );
};

/** Generic control for one constraint/property entry, shaped by its ExtensionDescriptor. */
const ExtensionField: React.FC<{
    name: string;
    descriptor: ExtensionDescriptor;
    value: string;
    fieldValueType: ValueType;
    onChange: (value: string) => void;
}> = ({ name, descriptor, value, fieldValueType, onChange }) => {
    const { valueType, description, required } = descriptor;
    const controlId = useId();
    const descriptionId = `${controlId}-description`;

    if (name === 'options' && valueType.includes('selectOptions')) {
        return (
            <OptionsField
                value={value}
                onChange={onChange}
                description={description}
                required={required}
                descriptionId={descriptionId}
                fieldValueType={fieldValueType}
            />
        );
    }

    if (valueType.length === 1 && valueType[0] === 'boolean') {
        return (
            <div>
                <div className="flex items-center">
                    <label className="flex items-center gap-1.5 cursor-pointer select-none" htmlFor={controlId}>
                        <input
                            id={controlId}
                            type="checkbox"
                            checked={value === 'true'}
                            onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
                            className={checkboxCls}
                            aria-describedby={descriptionId}
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                            {formatPropertyLabel(name)}{required ? ' *' : ''}
                        </span>
                    </label>
                    <DescriptionTooltip id={descriptionId} description={description} />
                </div>
            </div>
        );
    }

    if (valueType.includes('base64')) {
        return (
            <div>
                <div className="mb-1 flex h-4 items-center">
                    <label htmlFor={controlId} className={extensionLabelCls}>{formatPropertyLabel(name)}{required ? ' *' : ''}</label>
                    <DescriptionTooltip id={descriptionId} description={description} />
                </div>
                <input
                    id={controlId}
                    type="file"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => onChange(String(reader.result).split(',')[1] || '');
                        reader.readAsDataURL(file);
                    }}
                    className={`${inputCls} file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[10px] file:bg-teal-50 file:text-teal-700`}
                    aria-describedby={descriptionId}
                />
            </div>
        );
    }

    const isNumeric = valueType.length === 1 && (valueType[0] === 'integer' || valueType[0] === 'float');

    return (
        <div>
            <div className="mb-1 flex h-4 items-center">
                <label htmlFor={controlId} className={extensionLabelCls}>{formatPropertyLabel(name)}{required ? ' *' : ''}</label>
                <DescriptionTooltip id={descriptionId} description={description} />
            </div>
            <input
                id={controlId}
                type={isNumeric ? 'number' : 'text'}
                step={valueType[0] === 'float' ? 'any' : undefined}
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={valueType.join(' | ')}
                className={`${inputCls} ${valueType.includes('regex') ? 'font-mono' : ''}`}
                aria-describedby={descriptionId}
            />
        </div>
    );
};

const DefaultValueInput: React.FC<{
    field: PssFormField;
    resolvedInput: FieldInput;
    onChange: (val: string) => void;
}> = ({ field, resolvedInput, onChange }) => {
    if (field.defaultValueMode === 'expression') {
        return (
            <input
                type="text"
                value={field.defaultValue || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder="${expression}"
                className={`${inputCls} font-mono`}
            />
        );
    }

    if (field.type === 'bool' && resolvedInput !== 'select' && resolvedInput !== 'dialog') {
        return (
            <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input
                    type="checkbox"
                    checked={field.defaultValue === 'true'}
                    onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
                    className={checkboxCls}
                />
                <span className="text-xs text-slate-600 dark:text-slate-400">{field.defaultValue === 'true' ? 'True' : 'False'}</span>
            </label>
        );
    }

    if (resolvedInput === 'select' || resolvedInput === 'dialog') {
        let options: Record<string, unknown> | null = null;
        try {
            const p = field.properties.options ? JSON.parse(field.properties.options) : null;
            if (p && typeof p === 'object' && !Array.isArray(p)) options = p;
        } catch {
            // datasource-backed options -> no static list to default from
        }

        if (options) {
            return (
                <select value={field.defaultValue || ''} onChange={(e) => onChange(e.target.value)} className={inputCls}>
                    <option value="">— None —</option>
                    {Object.entries(options).map(([label, optionValue]) => (
                        <option key={label} value={String(optionValue)}>{label} ({String(optionValue)})</option>
                    ))}
                </select>
            );
        }
        if (field.type === 'enum') {
            return (
                <input
                    type="text"
                    value={field.defaultValue || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Default option value"
                    className={inputCls}
                />
            );
        }
    }

    if (field.type === 'date' || field.type === 'datetime') {
        return (
            <input
                type="text"
                value={field.defaultValue || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={field.type === 'date' ? '2026-01-01' : '2026-01-01T10:00:00'}
                className={inputCls}
            />
        );
    }

    if (field.type === 'number') {
        return (
            <input
                type="number"
                step={resolvedInput === 'float' ? 'any' : 1}
                value={field.defaultValue || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Enter a number"
                className={inputCls}
            />
        );
    }

    return (
        <input
            type="text"
            value={field.defaultValue || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Default value"
            className={inputCls}
        />
    );
};

// --- Main Component ---

export const PssFormBuilderTab: React.FC<PssFormBuilderTabProps> = ({ data, onChange }) => {
    const schema = data.formSchema;
    const [fields, setFields] = useState<PssFormField[]>(() => (schema?.fields || []).map(deserializePssFormField));
    const [showModifiers, setShowModifiers] = useState(false);
    const lastEmittedSchema = useRef<PssFormBuilderData['formSchema']>(undefined);

    useEffect(() => {
        if (schema === lastEmittedSchema.current) return;
        setFields((schema?.fields || []).map(deserializePssFormField));
    }, [schema]);

    const updateFields = (newFields: PssFormField[]) => {
        setFields(newFields);
        const serializedSchema = { ...(schema || {}), fields: newFields.map(serializePssFormField) };
        lastEmittedSchema.current = serializedSchema;
        onChange('formSchema', serializedSchema);
    };

    const patchField = (index: number, patch: Partial<PssFormField>) => {
        const newFields = [...fields];
        newFields[index] = { ...newFields[index], ...patch };
        updateFields(newFields);
    };

    const handleAddField = () => {
        const newField: PssFormField = {
            id: `field_${Date.now()}`,
            label: 'New Field',
            key: `field_${fields.length + 1}`,
            type: 'string',
            defaultValueMode: 'literal',
            constraints: {},
            properties: {},
        };
        updateFields([...fields, newField]);
    };

    const handleRemoveField = (index: number) => {
        updateFields(fields.filter((_, i) => i !== index));
    };

    const handleTypeChange = (index: number, newType: ValueType) => {
        const field = fields[index];
        const keptInput = field.input && inputAllowedValueTypes[field.input]?.includes(newType) ? field.input : undefined;
        patchField(index, { type: newType, input: keptInput, defaultValue: undefined, defaultValueMode: 'literal' });
    };

    const handleConstraintChange = (index: number, name: string, value: string) => {
        const next = { ...fields[index].constraints };
        if (value === '') delete next[name]; else next[name] = value;
        patchField(index, { constraints: next });
    };

    const handlePropertyChange = (index: number, name: string, value: string) => {
        const next = { ...fields[index].properties };
        if (value === '') delete next[name]; else next[name] = value;
        patchField(index, { properties: next });
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-100 dark:border-teal-900 flex gap-3">
                <FileText className="text-teal-600 dark:text-teal-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-teal-800 dark:text-teal-300">PSS Form Configuration</p>
                    <p className="text-[11px] text-teal-700 dark:text-teal-400 leading-relaxed">
                        Define form fields, default values, visibility modifiers, and enumeration options for the PSS host.
                    </p>
                </div>
            </div>

            {/* Form-wide Modifiers */}
            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                <button
                    onClick={() => setShowModifiers(!showModifiers)}
                    className="w-full px-4 py-2.5 flex items-center justify-between bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                    <span className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                        <Settings2 size={12} /> Form Modifiers
                    </span>
                    {showModifiers ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                </button>
                {showModifiers && (
                    <div className="p-4 border-t border-slate-200 dark:border-slate-800 space-y-2">
                        <p className="text-[10px] text-slate-500 leading-relaxed">
                            JSON object defining conditional visibility/required rules. Fields reference modifiers by name.
                        </p>
                        <textarea
                            value={data.formModifiers || '{\n\n}'}
                            onChange={(e) => onChange('formModifiers', e.target.value)}
                            rows={8}
                            className={`${inputCls} font-mono`}
                            placeholder='{\n  "modifierName": [\n    { "key": "hidden", "query": "$.fieldId", "equals": "value" }\n  ]\n}'
                        />
                    </div>
                )}
            </div>

            {/* Field List */}
            <div className="space-y-4">
                {fields.map((field, index) => {
                    const resolvedInput = resolveInput(field);
                    const constraintDescriptors = fieldConstraints[resolvedInput] || {};
                    const propertyDescriptors = { ...commonProperties, ...(fieldProperties[resolvedInput] || {}) };
                    const constraintEntries = Object.entries(constraintDescriptors);
                    const constraintFlags = constraintEntries.filter(([, descriptor]) => descriptor.valueType.length === 1 && descriptor.valueType[0] === 'boolean');
                    const constraintInputs = constraintEntries.filter(([, descriptor]) => descriptor.valueType.length !== 1 || descriptor.valueType[0] !== 'boolean');
                    const propertyEntries = Object.entries(propertyDescriptors);
                    const propertyFlags = propertyEntries.filter(([, descriptor]) => descriptor.valueType.length === 1 && descriptor.valueType[0] === 'boolean');
                    const propertyInputs = propertyEntries.filter(([, descriptor]) => descriptor.valueType.length !== 1 || descriptor.valueType[0] !== 'boolean');
                    const isHidden = field.properties.hidden === 'true';
                    const isDisabled = field.properties.disabled === 'true';

                    return (
                        <div key={field.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                            {/* Field Header */}
                            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50">
                                <div className="flex items-center gap-2">
                                    <GripVertical size={14} className="text-slate-300 cursor-move" />
                                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                                        {field.label || `Field #${index + 1}`}
                                    </span>
                                    {isHidden && <span className="text-[9px] px-1.5 py-0.5 bg-slate-200 dark:bg-slate-800 rounded text-slate-500">Hidden</span>}
                                    {isDisabled && <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 rounded text-amber-600">Disabled</span>}
                                </div>
                                <button onClick={() => handleRemoveField(index)} className="text-slate-400 hover:text-red-500 transition-colors">
                                    <Trash2 size={14} />
                                </button>
                            </div>

                            <div className="p-3 space-y-3">
                                {/* Row 1: Label + Key */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelCls}>Label</label>
                                        <input type="text" value={field.label} onChange={(e) => patchField(index, { label: e.target.value })} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Variable Key</label>
                                        <input type="text" value={field.key} onChange={(e) => patchField(index, { key: e.target.value })} className={`${inputCls} font-mono`} />
                                    </div>
                                </div>

                                {/* Row 2: Value Type (pivot) + Input (filtered to what's applicable) */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelCls}>Value Type</label>
                                        <select value={field.type} onChange={(e) => handleTypeChange(index, e.target.value as ValueType)} className={inputCls}>
                                            {valueTypes.map((t) => <option key={t} value={t}>{VALUE_TYPE_LABELS[t]}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelCls}>Input</label>
                                        <select value={resolvedInput} onChange={(e) => patchField(index, { input: e.target.value as FieldInput })} className={inputCls}>
                                            {applicableInputs(field.type).map((input) => (
                                                <option key={input} value={input}>
                                                    {FIELD_INPUT_LABELS[input]}{input === defaultFieldForValueType[field.type] ? ' (default)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Default Value — shaped by value type; "present" fields don't collect a value */}
                                {field.type !== 'present' && (
                                    <div>
                                        <div className="mb-1 flex items-center justify-between gap-3">
                                            <label className={extensionLabelCls}>Default Value</label>
                                            <div className="inline-flex h-6 shrink-0 overflow-hidden rounded border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900" role="group" aria-label="Default value mode">
                                                {(['literal', 'expression'] as const).map((mode) => {
                                                    const selected = (field.defaultValueMode || 'literal') === mode;
                                                    return (
                                                        <button
                                                            key={mode}
                                                            type="button"
                                                            aria-pressed={selected}
                                                            onClick={() => patchField(index, { defaultValueMode: mode })}
                                                            className={`px-2 text-[9px] font-semibold transition-colors ${selected
                                                                ? 'bg-slate-700 text-white dark:bg-slate-200 dark:text-slate-900'
                                                                : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
                                                            }`}
                                                        >
                                                            {mode === 'literal' ? 'Literal' : 'Expression'}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <DefaultValueInput
                                            field={field}
                                            resolvedInput={resolvedInput}
                                            onChange={(val) => patchField(index, { defaultValue: val })}
                                        />
                                    </div>
                                )}

                                {/* Constraints — filtered to what the resolved input supports */}
                                {constraintEntries.length > 0 && (
                                    <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
                                        <label className={labelCls}>Constraints</label>
                                        {constraintFlags.length > 0 && (
                                            <div className="mt-2 flex min-h-7 flex-wrap items-center gap-x-6 gap-y-2">
                                                {constraintFlags.map(([name, descriptor]) => (
                                                    <ExtensionField
                                                        key={name}
                                                        name={name}
                                                        descriptor={descriptor}
                                                        fieldValueType={field.type}
                                                        value={field.constraints[name] ?? ''}
                                                        onChange={(val) => handleConstraintChange(index, name, val)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        {constraintInputs.length > 0 && (
                                            <div className={`${constraintFlags.length > 0 ? 'mt-3' : 'mt-2'} grid grid-cols-2 items-start gap-x-3 gap-y-3`}>
                                                {constraintInputs.map(([name, descriptor]) => (
                                                    <ExtensionField
                                                        key={name}
                                                        name={name}
                                                        descriptor={descriptor}
                                                        fieldValueType={field.type}
                                                        value={field.constraints[name] ?? ''}
                                                        onChange={(val) => handleConstraintChange(index, name, val)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Properties — common properties + properties specific to the resolved input */}
                                <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
                                    <label className={labelCls}>Properties</label>
                                    {propertyFlags.length > 0 && (
                                        <div className="mt-2 flex min-h-7 flex-wrap items-center gap-x-6 gap-y-2">
                                            {propertyFlags.map(([name, descriptor]) => (
                                                <ExtensionField
                                                    key={name}
                                                    name={name}
                                                    descriptor={descriptor}
                                                    fieldValueType={field.type}
                                                    value={field.properties[name] ?? ''}
                                                    onChange={(val) => handlePropertyChange(index, name, val)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {propertyInputs.length > 0 && (
                                        <div className={`${propertyFlags.length > 0 ? 'mt-3' : 'mt-2'} grid grid-cols-2 items-start gap-x-3 gap-y-3`}>
                                            {propertyInputs.map(([name, descriptor]) => (
                                                <div key={name} className={name === 'options' || descriptor.valueType.includes('base64') ? 'col-span-2' : undefined}>
                                                    <ExtensionField
                                                        name={name}
                                                        descriptor={descriptor}
                                                        fieldValueType={field.type}
                                                        value={field.properties[name] ?? ''}
                                                        onChange={(val) => handlePropertyChange(index, name, val)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Add Field Button */}
                <button
                    onClick={handleAddField}
                    className="w-full py-2.5 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl text-slate-500 hover:text-teal-600 hover:border-teal-400 hover:bg-teal-50 dark:hover:bg-slate-900 transition-all flex items-center justify-center gap-2 text-xs font-bold"
                >
                    <Plus size={14} /> Add Form Field
                </button>
            </div>
        </div>
    );
};
